/**
 * The fixtures standing in a wall's door and window openings: the panels of
 * every door type (swing, pair, bifold, bypass, pocket, sliding glass,
 * garage) and window glass. Each draws into the render mesh and records its
 * export solids; every door panel the editor can pick also registers its own
 * pick target.
 *
 * A fixture is placed in its opening's wall frame: `s` along the wall leaf's
 * inner line and `p` feet into the room from the inner face, so the wall
 * itself occupies p ∈ [−WALL_THICKNESS, 0].
 */

import { type DoorKind, type DoorLeaf, type DoorSwingShape, doorSwingShape, isKnownDoorSwing, type Point, WALL_THICKNESS } from '@kaiizen/kai-floor-plan';
import { DOOR_SWING_LABELS, PALETTE, type Selection } from '../editor-model';
import { type Vec3 } from '../math3d';
import { type MeshBuilder, type RGB, type SceneSolidFolder } from './mesh3d';
import { type Pickable3D } from './pick3d';

const DOOR_THICKNESS = 1 / 12;
const GLASS: RGB = [0.45, 0.72, 0.86];
const DOOR_RGB: RGB = PALETTE.door;
/** Where window glass stands: halfway through the wall's thickness. */
const MID_REVEAL = -WALL_THICKNESS / 2;
/** A swing door's arc draws this far above the door's base. */
const ARC_LIFT = 0.03;
const ARC_SEGMENTS = 12;
/** A garage door's sectional panel, about 2 inches thick. */
const GARAGE_PANEL_THICKNESS = 0.15;
/** How far the garage door's grooves stop short of the jambs, and how far they stand proud of its face. */
const GROOVE_INSET = 0.05;
const GROOVE_LIFT = 0.002;

/** A floor-plane point in the opening's wall frame: `s` along the leaf, `p` into the room. */
export interface WallFramePoint {
  s: number;
  p: number;
}

/**
 * One door or window opening of a wall leaf, as its fixture builders see it:
 * the mesh to draw into, the wall frame's world and plan points, the
 * opening's span along the leaf and its height span, the leaf's world unit
 * vectors along it and into the room, and whether the opening is selected.
 */
export interface WallOpening {
  meshBuilder: MeshBuilder;
  worldPoint: (s: number, p: number, height: number) => Vec3;
  planPoint: (s: number, p: number) => Point;
  start: number;
  end: number;
  base: number;
  top: number;
  alongNormal: Vec3;
  inwardNormal: Vec3;
  selected: boolean;
}

/** Where a door's pickable panels register: the selection they pick as, their wall's cull group, and the scene's pick targets. */
export interface PanelTargets {
  selection: Selection;
  group: number;
  pickables: Pickable3D[];
}

/** A door opening as its panel builders see it: the opening plus its export folder, its color, and the panel registration. */
interface DoorFixture extends WallOpening {
  folder: SceneSolidFolder;
  color: RGB;
  /** Registers the door panel spanning floor points `from`→`to` as an export solid and, for a pickable door, a pick target. */
  addPanel: (from: WallFramePoint, to: WallFramePoint) => void;
}

/** Window glass at mid-reveal, with a mullion each way through its middle; the glass tints while the window is selected. */
export function buildWindow({ meshBuilder, worldPoint, planPoint, start, end, base, top, inwardNormal, selected }: WallOpening): void {
  meshBuilder.solid({
    layer: 'glass',
    folder: { name: 'Window' },
    rings: [[planPoint(start, MID_REVEAL), planPoint(end, MID_REVEAL)]],
    closed: false,
    base,
    top,
  });
  meshBuilder.glassQuad(
    worldPoint(start, MID_REVEAL, base),
    worldPoint(end, MID_REVEAL, base),
    worldPoint(end, MID_REVEAL, top),
    worldPoint(start, MID_REVEAL, top),
    inwardNormal,
    selected ? PALETTE.accent : GLASS,
  );
  const middle = (start + end) / 2, middleHeight = (base + top) / 2;
  meshBuilder.line(worldPoint(middle, MID_REVEAL, base), worldPoint(middle, MID_REVEAL, top));
  meshBuilder.line(worldPoint(start, MID_REVEAL, middleHeight), worldPoint(end, MID_REVEAL, middleHeight));
}

/**
 * The door of type `swing` in its opening, drawn by its kind, every solid of
 * it filed under one export folder named for the type. `door` is the index
 * of the door's box; `panelTargets`, when the door is pickable, is where its
 * panels register.
 */
export function buildDoor(opening: WallOpening, swing: string, door: number, panelTargets: PanelTargets | null): void {
  const folder: SceneSolidFolder = { name: `Door - ${isKnownDoorSwing(swing) ? DOOR_SWING_LABELS[swing].exportFolder : swing}` };
  const fixture: DoorFixture = {
    ...opening,
    folder,
    color: opening.selected ? PALETTE.accent : DOOR_RGB,
    addPanel: (from, to) => {
      opening.meshBuilder.solid({
        layer: 'door',
        door,
        folder,
        rings: [[opening.planPoint(from.s, from.p), opening.planPoint(to.s, to.p)]],
        closed: false,
        width: DOOR_THICKNESS,
        base: opening.base,
        top: opening.top,
      });
      if (panelTargets) panelTargets.pickables.push(panelPickable(opening, from, to, panelTargets));
    },
  };
  const shape = doorSwingShape(swing);
  DOOR_BUILDERS[shape.kind](fixture, shape);
}

/** A pickable rect through a thin panel's center plane, spanning the opening's height. */
function panelPickable({ worldPoint, base, top }: WallOpening, from: WallFramePoint, to: WallFramePoint, { selection, group }: PanelTargets): Pickable3D {
  const start = worldPoint(from.s, from.p, base), end = worldPoint(to.s, to.p, base);
  const deltaX = end[0] - start[0], deltaZ = end[2] - start[2];
  const length = Math.hypot(deltaX, deltaZ) || 1;
  return { sel: selection, group, kind: 'rect', origin: start, along: [deltaX / length, 0, deltaZ / length], sLen: length, yLen: top - base };
}

/** The world normal of a direction mixing `along` parts of the leaf's along vector and `into` parts of its inward one, on the floor plane. */
function floorPlaneNormal(alongNormal: Vec3, inwardNormal: Vec3, along: number, into: number): Vec3 {
  const x = alongNormal[0] * along + inwardNormal[0] * into, z = alongNormal[2] * along + inwardNormal[2] * into;
  const length = Math.hypot(x, z) || 1;
  return [x / length, 0, z / length];
}

/**
 * A vertical box `thickness` through, standing between two floor points in
 * any direction and spanning the door's height. It is outlined on one broad
 * face: the outer one for a box running from the opening's start toward its
 * end.
 */
function panelBox(door: DoorFixture, from: WallFramePoint, to: WallFramePoint, thickness: number): void {
  const { meshBuilder, worldPoint, base, top, alongNormal, inwardNormal, color } = door;
  const deltaS = to.s - from.s, deltaP = to.p - from.p;
  const length = Math.hypot(deltaS, deltaP) || 1;
  const alongS = deltaS / length, alongP = deltaP / length;
  const normalS = alongP, normalP = -alongS;
  const halfThickness = thickness / 2;
  const corner = (point: WallFramePoint, sign: number, height: number) =>
    worldPoint(point.s + normalS * halfThickness * sign, point.p + normalP * halfThickness * sign, height);
  meshBuilder.quad(
    corner(from, 1, base),
    corner(to, 1, base),
    corner(to, 1, top),
    corner(from, 1, top),
    floorPlaneNormal(alongNormal, inwardNormal, normalS, normalP),
    color,
  );
  meshBuilder.quad(
    corner(from, -1, base),
    corner(to, -1, base),
    corner(to, -1, top),
    corner(from, -1, top),
    floorPlaneNormal(alongNormal, inwardNormal, -normalS, -normalP),
    color,
  );
  meshBuilder.quad(
    corner(from, 1, base),
    corner(from, -1, base),
    corner(from, -1, top),
    corner(from, 1, top),
    floorPlaneNormal(alongNormal, inwardNormal, -alongS, -alongP),
    color,
  );
  meshBuilder.quad(
    corner(to, 1, base),
    corner(to, -1, base),
    corner(to, -1, top),
    corner(to, 1, top),
    floorPlaneNormal(alongNormal, inwardNormal, alongS, alongP),
    color,
  );
  meshBuilder.quad(corner(from, 1, top), corner(to, 1, top), corner(to, -1, top), corner(from, -1, top), [0, 1, 0], color);
  meshBuilder.polyline([corner(from, 1, base), corner(to, 1, base), corner(to, 1, top), corner(from, 1, top)], true);
}

/** A thin door panel between two floor points: registered, and drawn as a box. */
function doorPanel(door: DoorFixture, from: WallFramePoint, to: WallFramePoint): void {
  door.addPanel(from, to);
  panelBox(door, from, to, DOOR_THICKNESS);
}

/**
 * One hinged leaf standing open: a 1-inch-thick panel hinged at its jamb,
 * hung on the face of the wall it opens toward (in-swing on the room-side
 * face, out-swing on the outer face) and spanning the door's own height, so
 * a raised door sits at the right height; plus its swing arc, drawn just
 * above the door's base. Its box runs outward, from its end farther into
 * the room, so every leaf is outlined on its face toward the opening's start.
 */
function buildSwingLeaf(door: DoorFixture, { alongSign, intoSign, widthShare }: DoorLeaf): void {
  const hinge = alongSign > 0 ? door.start : door.end, length = (door.end - door.start) * widthShare;
  const wallFace = intoSign > 0 ? 0 : -WALL_THICKNESS;
  const hingeEnd = { s: hinge, p: wallFace }, swungEnd = { s: hinge, p: wallFace + intoSign * length };
  door.addPanel(hingeEnd, swungEnd);
  const [innerEnd, outerEnd] = intoSign > 0 ? [swungEnd, hingeEnd] : [hingeEnd, swungEnd];
  panelBox(door, innerEnd, outerEnd, DOOR_THICKNESS);
  const arc = Array.from({ length: ARC_SEGMENTS + 1 }, (_, step) => {
    const angle = (step / ARC_SEGMENTS) * (Math.PI / 2);
    return door.worldPoint(hinge + alongSign * length * Math.cos(angle), wallFace + intoSign * length * Math.sin(angle), door.base + ARC_LIFT);
  });
  door.meshBuilder.polyline(arc, false);
}

/** 4-panel bifold, rendered partly folded from the wall's center plane: each half zigzags into the room. */
function buildBifold(door: DoorFixture): void {
  const quarter = (door.end - door.start) / 4;
  const cos = Math.cos(Math.PI / 6), sin = Math.sin(Math.PI / 6);
  const plane = -WALL_THICKNESS / 2;
  for (const half of [{ jamb: door.start, direction: 1 }, { jamb: door.end, direction: -1 }]) {
    const fold: WallFramePoint = { s: half.jamb + half.direction * quarter * cos, p: plane + quarter * sin };
    doorPanel(door, { s: half.jamb, p: plane }, fold);
    doorPanel(door, fold, { s: half.jamb + half.direction * 2 * quarter * cos, p: plane });
  }
}

/** Bypass sliders: two flat overlapping panels on parallel tracks, centered in the wall's thickness. */
function buildBypass(door: DoorFixture): void {
  const { start, end } = door;
  const width = end - start, lap = Math.min(0.5, width * 0.12);
  const center = -WALL_THICKNESS / 2, gap = 0.02;
  const rear = center - DOOR_THICKNESS / 2 - gap, front = center + DOOR_THICKNESS / 2 + gap;
  doorPanel(door, { s: start, p: rear }, { s: start + width / 2 + lap, p: rear });
  doorPanel(door, { s: end - width / 2 - lap, p: front }, { s: end, p: front });
}

/**
 * Pocket door: the visible half of the panel, slid halfway out of its wall
 * cavity: a door-material panel centered in the wall's thickness covering
 * the half of the opening on its hand's side (matching the 2D symbol), open
 * beside it.
 */
function buildPocket(door: DoorFixture, { hand }: DoorSwingShape): void {
  const middle = (door.start + door.end) / 2;
  const p = -WALL_THICKNESS / 2;
  doorPanel(door, { s: hand === 'left' ? door.start : middle, p }, { s: hand === 'left' ? middle : door.end, p });
}

/**
 * Sliding glass door: two window-style glazed panels on parallel tracks
 * within the opening, each with a center mullion. The pair is centered in
 * the wall's thickness, the fixed panel outer-side and the slider room-side;
 * the slider is on the door's hand. Its panels sit within the opening's
 * footprint, so the opening's own pick target covers them.
 */
function buildSlidingGlass(
  { meshBuilder, worldPoint, planPoint, folder, start, end, base, top, inwardNormal, selected }: DoorFixture,
  { hand }: DoorSwingShape,
): void {
  const width = end - start, lap = Math.min(0.5, width * 0.12), left = hand === 'left';
  const glass = selected ? PALETTE.accent : GLASS;
  const pane = (paneStart: number, paneEnd: number, p: number) => {
    const corner = (s: number, height: number) => worldPoint(s, p, height);
    meshBuilder.solid({ layer: 'glass', folder, rings: [[planPoint(paneStart, p), planPoint(paneEnd, p)]], closed: false, base, top });
    meshBuilder.glassQuad(corner(paneStart, base), corner(paneEnd, base), corner(paneEnd, top), corner(paneStart, top), inwardNormal, glass);
    meshBuilder.polyline([corner(paneStart, base), corner(paneEnd, base), corner(paneEnd, top), corner(paneStart, top)], true);
    meshBuilder.line(corner((paneStart + paneEnd) / 2, base), corner((paneStart + paneEnd) / 2, top));
  };
  const slider: [number, number] = left ? [start, start + width / 2 + lap] : [end - width / 2 - lap, end];
  const fixed: [number, number] = left ? [end - width / 2 - lap, end] : [start, start + width / 2 + lap];
  pane(fixed[0], fixed[1], -WALL_THICKNESS * 0.65);
  pane(slider[0], slider[1], -WALL_THICKNESS * 0.35);
}

/**
 * Overhead garage door, closed: a sectional panel filling the opening,
 * flush with the wall's inner face, with three grooves across its room-side
 * face. Its box runs from the end jamb to the start, so its outlined face is
 * the room-side one. It sits within the opening's footprint, so the
 * opening's own pick target covers it.
 */
function buildGarage(door: DoorFixture): void {
  const { meshBuilder, worldPoint, planPoint, folder, start, end, base, top } = door;
  const back = -GARAGE_PANEL_THICKNESS;
  meshBuilder.solid({
    layer: 'door',
    folder,
    rings: [[planPoint(start, back), planPoint(end, back), planPoint(end, 0), planPoint(start, 0)]],
    closed: true,
    base,
    top,
  });
  panelBox(door, { s: end, p: back / 2 }, { s: start, p: back / 2 }, GARAGE_PANEL_THICKNESS);
  for (let section = 1; section < 4; section++) {
    const height = base + (section * (top - base)) / 4;
    meshBuilder.line(worldPoint(start + GROOVE_INSET, GROOVE_LIFT, height), worldPoint(end - GROOVE_INSET, GROOVE_LIFT, height));
  }
}

/** How each kind of door stands in its opening. */
const DOOR_BUILDERS: Record<DoorKind, (door: DoorFixture, shape: DoorSwingShape) => void> = {
  hinged: (door, { leaves }) => leaves.forEach(leaf => buildSwingLeaf(door, leaf)),
  pocket: buildPocket,
  bifold: buildBifold,
  bypass: buildBypass,
  garage: buildGarage,
  slidingGlass: buildSlidingGlass,
};
